import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Breadcrumb,
  Modal,
  message,
  Space,
  Row,
  Col,
  Grid,
  Card,
  Typography,
  Tag,
  Tooltip,
  Empty,
  Switch,
  Badge,
} from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
  DeleteOutlined,
  EditOutlined,
  RightOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  CrownOutlined,
  CustomerServiceOutlined,
  DollarOutlined,
  ShopOutlined,
  SettingOutlined,
  LockOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { rolesService } from '../../common/services';
import Can from '../../components/can';

const { Title, Text } = Typography;

// Mapping des icônes
const ICON_MAP = {
  crown: <CrownOutlined />,
  'customer-service': <CustomerServiceOutlined />,
  dollar: <DollarOutlined />,
  shop: <ShopOutlined />,
  setting: <SettingOutlined />,
  lock: <LockOutlined />,
};

const Roles = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [isLoading, setLoading] = useState(false);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await rolesService.getAll(true);
      if (response?.success) {
        setRoles(response.data || []);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error('Erreur chargement rôles:', error);
      message.error('Erreur lors du chargement des rôles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDelete = (role) => {
    if (role.isSystem) {
      message.warning('Impossible de supprimer un rôle système');
      return;
    }

    Modal.confirm({
      title: 'Confirmer la suppression',
      icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div style={{ marginTop: 16 }}>
          <Text>Êtes-vous sûr de vouloir supprimer ce rôle ?</Text>
          <Card size="small" style={{ marginTop: 12, backgroundColor: '#fafafa' }}>
            <Space>
              <Tag color={role.color} icon={ICON_MAP[role.icon]}>
                {role.label}
              </Tag>
              <Text type="secondary">({role.permissions?.length || 0} permissions)</Text>
            </Space>
          </Card>
        </div>
      ),
      okText: 'Supprimer',
      cancelText: 'Annuler',
      okButtonProps: { danger: true },
      centered: true,
      onOk: async () => {
        try {
          await rolesService.delete(role._id);
          message.success('Rôle supprimé avec succès');
          setRoles((prev) => prev.filter((r) => r._id !== role._id));
        } catch (error) {
          console.error('Erreur suppression:', error);
          message.error(error?.message || 'Erreur lors de la suppression');
        }
      },
    });
  };

  const handleToggle = async (role, checked) => {
    if (role.isSystem && role.isActive) {
      message.warning('Impossible de désactiver un rôle système');
      return;
    }

    try {
      await rolesService.toggle(role._id);
      setRoles((prev) =>
        prev.map((r) =>
          r._id === role._id ? { ...r, isActive: checked } : r
        )
      );
      message.success(`Rôle ${checked ? 'activé' : 'désactivé'} avec succès`);
    } catch (error) {
      console.error('Erreur toggle:', error);
      message.error(error?.message || 'Erreur lors de la modification');
    }
  };

  const columns = [
    {
      title: 'Rôle',
      key: 'role',
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Space>
            <Tag
              color={record.color}
              icon={ICON_MAP[record.icon]}
              style={{ borderRadius: 6, padding: '4px 10px' }}
            >
              {record.label}
            </Tag>
            {record.isSystem && (
              <Tooltip title="Rôle système (non modifiable)">
                <LockOutlined style={{ color: '#8c8c8c' }} />
              </Tooltip>
            )}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.description || 'Aucune description'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Nom technique',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (name) => (
        <Text code style={{ fontSize: 12 }}>
          {name}
        </Text>
      ),
      responsive: ['md'],
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions',
      key: 'permissions',
      width: 120,
      align: 'center',
      render: (permissions) => (
        <Badge
          count={permissions?.length || 0}
          style={{ backgroundColor: '#52c41a' }}
          showZero
        />
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      align: 'center',
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={(checked) => handleToggle(record, checked)}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<CloseCircleOutlined />}
          disabled={record.isSystem && record.isActive}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space size={4}>
          <Can perm="roles:update">
            <Tooltip title="Modifier">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => navigate(`/dashboard/modifier-role/${record._id}`)}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
          </Can>
          <Can perm="roles:delete">
            <Tooltip title={record.isSystem ? 'Rôle système' : 'Supprimer'}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
                disabled={record.isSystem}
              />
            </Tooltip>
          </Can>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb
        separator={<RightOutlined />}
        style={{ marginBottom: 20 }}
        items={[
          { title: <Link to="/dashboard">Accueil</Link> },
          { title: 'Administration' },
          { title: <span style={{ fontWeight: 600 }}>Rôles</span> },
        ]}
      />

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <Title
            level={isMobile ? 4 : 3}
            style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}
          >
            <SafetyCertificateOutlined style={{ color: '#722ed1' }} />
            Gestion des Rôles
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Gérez les rôles et leurs permissions
          </Text>
        </div>
        <Space wrap>
          <Button
            icon={<ReloadOutlined spin={isLoading} />}
            onClick={loadData}
            disabled={isLoading}
          >
            {!isMobile && 'Actualiser'}
          </Button>
          <Can perm="roles:create">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/dashboard/ajouter-role')}
            >
              {isMobile ? '' : 'Nouveau rôle'}
            </Button>
          </Can>
        </Space>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} md={6}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
              border: 'none',
              borderRadius: 16,
            }}
            bodyStyle={{ padding: isMobile ? 16 : 20 }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              Total
            </Text>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#722ed1' }}>
              {roles.length}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
              border: 'none',
              borderRadius: 16,
            }}
            bodyStyle={{ padding: isMobile ? 16 : 20 }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              Actifs
            </Text>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#52c41a' }}>
              {roles.filter((r) => r.isActive).length}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #fff7e6 0%, #ffd591 100%)',
              border: 'none',
              borderRadius: 16,
            }}
            bodyStyle={{ padding: isMobile ? 16 : 20 }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              Système
            </Text>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#fa8c16' }}>
              {roles.filter((r) => r.isSystem).length}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
        <Table
          rowKey={(r) => r._id?.toString()}
          columns={columns}
          dataSource={roles}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: 'max-content' }}
          loading={isLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: !isMobile,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {`${range[0]}-${range[1]} sur ${total}`}
              </Text>
            ),
            style: { padding: '12px 16px', margin: 0 },
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<Text type="secondary">Aucun rôle trouvé</Text>}
              />
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default Roles;
